<script setup lang="ts">
import billsHttpService from '@/services/http/billsHttpService.ts'
import { type BillStatus, BillStatuses, type Id } from '@raildev/shared-utils'
import { computed, ref, onMounted, reactive } from 'vue'
import type { BillAdminModel, BillItemAdminModel } from '@/services/http/httpResponses.ts'
import { PaymentMethods } from '@/constants.ts'
import { Roles } from '@/constants.ts'
import { useToast } from 'primevue/usetoast'

definePage({
  meta: {
    allowedRoles: [Roles.Admin],
  },
})

const toast = useToast()

type FilterParams = {
  id: Id | null
  transactionId: string | null
  status: BillStatus | null
  userId: Id | null
}

const filterParams = reactive<FilterParams>({
  id: null,
  transactionId: null,
  status: null,
  userId: null,
})

const billStatuses = Object.entries(BillStatuses)
  .filter(([_, value]) => value != BillStatuses.None)
  .map(([label, value]) => ({ label, value }))

const bills = ref<BillAdminModel[]>([])

const refreshBills = async () => {
  const billsResponse = await billsHttpService.getBills({
    id: filterParams.id,
    transactionId: filterParams.transactionId,
    status: filterParams.status,
    userId: filterParams.userId,
  })
  bills.value = billsResponse.bills
}

const getStatus = (status: BillStatus) => {
  return billStatuses.find((s) => s.value == status)?.label
}

const isKaspiBill = (b: BillAdminModel) => b.paymentMethod === PaymentMethods.KaspiRemotePhone
const isPaid = (b: BillAdminModel) => b.status === BillStatuses.Success

const getPaymentMethodName = (m?: number | null) => {
  switch (m) {
    case PaymentMethods.TipTop:
      return 'TipTop (карта)'
    case PaymentMethods.KaspiRemotePhone:
      return 'Kaspi (удалённый счёт)'
    default:
      return ''
  }
}

const getStatusSeverity = (s: BillStatus) => {
  switch (s) {
    case BillStatuses.Success:
      return 'success'
    case BillStatuses.PaymentPending:
      return 'info'
    case BillStatuses.Processing:
      return 'warn'
    case BillStatuses.Created:
    case BillStatuses.None:
    default:
      return 'secondary'
  }
}

const canTakeInWork = (b: BillAdminModel) =>
  isKaspiBill(b) && !isPaid(b) && b.status === BillStatuses.Created
const canMarkSent = (b: BillAdminModel) =>
  isKaspiBill(b) && !isPaid(b) && b.status === BillStatuses.Processing
const canMarkPaid = (b: BillAdminModel) =>
  isKaspiBill(b) && !isPaid(b) && b.status === BillStatuses.PaymentPending
const kaspiActionInProgress = ref<Id | null>(null)
const isActionLoading = (billId: Id) => kaspiActionInProgress.value === billId

const changeKaspiStatus = async (bill: BillAdminModel, status: BillStatus, label: string) => {
  try {
    kaspiActionInProgress.value = bill.id
    await billsHttpService.updateKaspiBillStatus(bill.id, status)

    toast.add({
      severity: 'success',
      summary: 'Успех',
      detail: `Статус: ${label}`,
      life: 2500,
    })
    await refreshBills()
  } catch {
    toast.add({
      severity: 'error',
      summary: 'Ошибка',
      detail: 'Не удалось обновить статус',
      life: 3000,
    })
  } finally {
    kaspiActionInProgress.value = null
  }
}

const showActionsColumn = computed(() => true)

onMounted(async () => {
  const urlParams = new URLSearchParams(window.location.search)
  const id = urlParams.get('id')
  if (id) {
    filterParams.id = id as unknown as Id
  }

  await refreshBills()
})
</script>
<template>
  <div class="flex flex-col gap-4 w-full">
    <Toast />

    <div class="flex flex-row justify-between items-center w-full">
      <div class="flex gap-3">
        <InputText v-model="filterParams.transactionId" placeholder="# транзакции"></InputText>
        <Select
          :options="billStatuses"
          v-model="filterParams.status"
          option-label="label"
          option-value="value"
          fluid
          show-clear
          placeholder="Статус"
        ></Select>
        <Select
          :options="[]"
          v-model="filterParams.userId"
          option-label="label"
          fluid
          placeholder="Пользователь"
        ></Select>
      </div>
      <Button label="Применить" @click="refreshBills()" />
    </div>

    <DataTable ref="dt" :value="bills" dataKey="id" class="w-full">
      <template #header>
        <div class="flex flex-wrap gap-2 items-center justify-between"></div>
      </template>
      <Column header="Статус" style="min-width: 12rem">
        <template #body="slotProps">
          <Tag
            :value="getStatus(slotProps.data.status)"
            :severity="getStatusSeverity(slotProps.data.status)"
          />
        </template>
      </Column>
      <Column header="Дата создания" style="min-width: 12rem">
        <template #body="slotProps">
          {{ slotProps.data.createdDate.toLocaleString('ru-RU') }}
        </template>
      </Column>
      <Column header="Дата оплаты" style="min-width: 12rem">
        <template #body="slotProps">
          {{ slotProps.data.paidDate?.toLocaleString('ru-RU') }}
        </template>
      </Column>
      <Column header="Сумма" style="min-width: 12rem">
        <template #body="slotProps">
          {{
            slotProps.data.items.reduce(
              (sum: number, x: BillItemAdminModel) => sum + x.price * x.quantity,
              0,
            )
          }}
        </template>
      </Column>
      <Column header="# транзакции" style="min-width: 12rem">
        <template #body="slotProps">
          {{ slotProps.data.transactionId }}
        </template>
      </Column>
      <Column header="# refund транзакции" style="min-width: 12rem">
        <template #body="slotProps">
          {{ slotProps.data.refundTransactionId }}
        </template>
      </Column>
      <Column header="Пользователь" style="min-width: 12rem">
        <template #body="slotProps">
          {{ slotProps.data.user.name }}
        </template>
      </Column>
      <Column header="Способ" style="min-width: 12rem">
        <template #body="slotProps">
          {{ getPaymentMethodName(slotProps.data.paymentMethod) }}
        </template>
      </Column>
      <Column header="Телефон (Kaspi)" style="min-width: 12rem">
        <template #body="slotProps">
          {{ slotProps.data.customerPhone }}
        </template>
      </Column>
      <template #empty>
        <div class="flex flex-col items-center gap-2 py-6 text-gray-400">
          <i class="pi pi-inbox text-4xl" />
          <span>Данные отсутствуют</span>
        </div>
      </template>
      <Column v-if="showActionsColumn" header="Действия (Kaspi)" style="min-width: 18rem">
        <template #body="slotProps">
          <div
            class="flex flex-col gap-2 w-full max-w-64"
            v-if="isKaspiBill(slotProps.data) && !isPaid(slotProps.data)"
          >
            <Button
              class="w-full"
              size="small"
              label="Взять в работу"
              :disabled="!canTakeInWork(slotProps.data) || isActionLoading(slotProps.data.id)"
              @click="changeKaspiStatus(slotProps.data, BillStatuses.Processing, 'В работе')"
            />
            <Button
              class="w-full"
              size="small"
              label="Отправлено"
              severity="info"
              :disabled="!canMarkSent(slotProps.data) || isActionLoading(slotProps.data.id)"
              @click="changeKaspiStatus(slotProps.data, BillStatuses.PaymentPending, 'Отправлено')"
            />
            <Button
              class="w-full"
              size="small"
              label="Оплачено"
              severity="success"
              :disabled="!canMarkPaid(slotProps.data) || isActionLoading(slotProps.data.id)"
              @click="changeKaspiStatus(slotProps.data, BillStatuses.Success, 'Оплачено')"
            />
          </div>
        </template>
      </Column>
    </DataTable>
  </div>
</template>

<style scoped></style>
